import { ApolloError, useMutation } from '@apollo/client';
import type { VariablesOf } from '@graphql-typed-document-node/core';
import { SessionActions } from '../../account/sessionSlice';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import { gql } from '../../graphql/generated/gql';
import splitSubdomain from './splitSubdomain';

const UPDATE_ORGANIZATION_MUTATION = gql(/** @complexity 2 */ `
  mutation updateOrganization($name: String, $subdomain: String) {
    organization: updateOrganization(name: $name, subdomain: $subdomain) {
      id
      name
      host
      effectiveHost
    }
  }
`);

const useUpdateOrganization = ({ host }: { host: string }) => {
  const dispatch = useAppDispatch();
  const [updateOrganization] = useMutation(UPDATE_ORGANIZATION_MUTATION);
  const [, baseHost] = splitSubdomain(host);

  return (variables: VariablesOf<typeof UPDATE_ORGANIZATION_MUTATION>) =>
    updateOrganization({ variables })
      .then((response) => {
        if (response.data?.organization) {
          const {
            data: { organization },
          } = response;

          dispatch(
            SessionActions.setCurrentOrganization({
              effectiveHost: organization.effectiveHost,
              host: organization.host,
              name: organization.name,
            }),
          );
        }

        return response;
      })
      .catch((errors: ApolloError) => {
        if (!errors.graphQLErrors) {
          throw errors;
        }

        const graphQLErrors = errors.graphQLErrors.map(
          (error): typeof error => {
            let { message } = error;

            message = message
              .replace(
                /\[host\] should be at least ([0-9]+) character\(s\)/,
                (_m, limit) =>
                  `Subdomain should be at least ${
                    limit - (baseHost?.length ?? 0)
                  } characters`,
              )
              .replace(/\[host\]/, 'Subdomain');

            return {
              ...error,
              message,
            };
          },
        );

        throw new ApolloError({
          ...errors,
          graphQLErrors,
        });
      });
};

export default useUpdateOrganization;
